import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePlatformStatus } from '@/contexts/PlatformStatusContext';
import { useShopifyCartStore } from '@/stores/cartStore';
import { Logo } from './Logo';
import { WishlistBadge } from './WishlistBadge';
import { ShopifyCartDrawer } from './ShopifyCartDrawer';
import { Button } from './ui/button';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { AdminSettingsPanel } from './admin/AdminSettingsPanel';
import { AdminNavDropdown } from './admin/AdminNavDropdown';
import { toast } from 'sonner';
import { prefetchHandlers, prefetchRoute } from '@/lib/routePrefetch';
import {
  Home,
  ShoppingBag,
  Calendar,
  BookOpen,
  User,
  Award,
  LogOut,
  Menu,
  X,
  ShoppingCart,
  Heart,
  Package,
  Image,
  ChevronDown,
  ChevronRight,
  Gift,
  Shield,
  Settings,
  Shirt,
  Percent,
  UserPlus,
  Users,
  Megaphone,
  Link2,
  Gauge,
  Car,
  Trophy,
  Route,
} from 'lucide-react';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

interface NavLink {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  subLinks?: { name: string; href: string; icon: React.ComponentType<{ className?: string }> }[];
}

const mainNavLinks: NavLink[] = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'Shop', href: '/shop', icon: ShoppingBag },
  { 
    name: 'Events', 
    href: '/events', 
    icon: Calendar,
    subLinks: [
      { name: 'Event Images', href: '/event-albums', icon: Image }
    ]
  },
  { name: 'Blog', href: '/blog', icon: BookOpen },
  {
    name: 'Øutlaws Drive',
    href: '/drive',
    icon: Gauge,
    subLinks: [
      { name: 'My Garage', href: '/drive/garage', icon: Car },
      { name: 'My Drives', href: '/drive/drives', icon: Route },
      { name: 'My Performance', href: '/drive/performance', icon: Gauge },
      { name: 'Leaderboards', href: '/drive/leaderboards', icon: Trophy },

    ],
  },
];

const accountLinks: NavLink[] = [
  { name: 'My Account', href: '/account', icon: User },
  { name: 'Rewards', href: '/rewards', icon: Gift },
  { name: 'Orders', href: '/account/orders', icon: Package },
  { name: 'Wishlist', href: '/wishlist', icon: Heart },
];

// Admin-specific navigation links for dropdowns
const adminMainLinks = [
  { name: 'Dashboard', href: '/dashboard', icon: Home },
  { name: 'Shop', href: '/shop', icon: ShoppingBag },
  { name: 'Events', href: '/events', icon: Calendar },
  { name: 'Event Images', href: '/event-albums', icon: Image },
  { name: 'Blog', href: '/blog', icon: BookOpen },
];

const adminAccountLinks = [
  { name: 'Rewards', href: '/rewards', icon: Gift },
  { name: 'Orders', href: '/account/orders', icon: Package },
  { name: 'Wishlist', href: '/wishlist', icon: Heart },
];

const adminPanelLinks = [
  { name: 'Admin Panel', href: '/admin', icon: Shield },
  { name: 'Clothing & Accessories', href: '/admin/products', icon: Shirt },
  { name: 'Coupon Management', href: '/admin/coupons', icon: Percent },
  { name: 'Ambassador Referrals', href: '/admin/ambassadors', icon: UserPlus },
  { name: 'Sub-Admin Accounts', href: '/admin/sub-admins', icon: Users },
];

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAmbassador, setIsAmbassador] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSubAdmin, setIsSubAdmin] = useState(false);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [expandedNav, setExpandedNav] = useState<string | null>(null);
  const [expandedDropdowns, setExpandedDropdowns] = useState<Set<string>>(new Set());
  const { user, signOut } = useAuth();
  const { maintenanceMode } = usePlatformStatus();
  const itemCount = useShopifyCartStore((state) => state.itemCount)();
  const location = useLocation();
  const navigate = useNavigate();

  const isMaintenanceBlocked = maintenanceMode && !isAdmin;

  useEffect(() => {
    const checkRoles = async () => {
      if (!user) {
        setIsAdmin(false);
        setIsSubAdmin(false);
        setIsAmbassador(false);
        setRolesLoaded(true);
        return;
      }

      const { data: rolesData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);

      const roles = rolesData?.map(r => r.role) || [];
      setIsAdmin(roles.includes('admin'));
      setIsSubAdmin(roles.includes('sub_admin'));
      setIsAmbassador(roles.includes('ambassador'));
      setRolesLoaded(true);
    };
    checkRoles();
  }, [user]);

  // Auto-expand dropdown containing active route for admin users
  useEffect(() => {
    if (!isAdmin) return;
    
    const currentPath = location.pathname;
    const newExpanded = new Set<string>();
    
    if (adminMainLinks.some(link => link.href === currentPath)) {
      newExpanded.add('main');
    }
    if (adminAccountLinks.some(link => link.href === currentPath)) {
      newExpanded.add('account');
    }
    if (adminPanelLinks.some(link => link.href === currentPath) || currentPath.startsWith('/admin')) {
      newExpanded.add('admin');
    }
    
    setExpandedDropdowns(newExpanded);
  }, [location.pathname, isAdmin]);

  // Warm up sidebar route chunks during idle time so tab switches feel instant.
  useEffect(() => {
    if (!rolesLoaded) return;
    const idle =
      (window as unknown as { requestIdleCallback?: (cb: () => void) => number })
        .requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 1000));
    idle(() => {
      const paths = isAdmin
        ? [...adminMainLinks, ...adminAccountLinks, ...adminPanelLinks].map((l) => l.href)
        : [...mainNavLinks, ...accountLinks].map((l) => l.href).concat(['/ambassador']);
      paths.forEach(prefetchRoute);
    });
  }, [rolesLoaded, isAdmin]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleMaintenanceClick = (e: React.MouseEvent) => {
    if (isMaintenanceBlocked) {
      e.preventDefault();
      toast("Website is undergoing maintenance", {
        description: "Please check back shortly.",
      });
    }
  };

  const toggleDropdown = (key: string) => {
    setExpandedDropdowns(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const renderAdminNavigation = () => (
    <>
      <AdminNavDropdown
        title="Main"
        icon={Home}
        links={adminMainLinks}
        isExpanded={expandedDropdowns.has('main')}
        onToggle={() => toggleDropdown('main')}
        currentPath={location.pathname}
        onLinkClick={() => setSidebarOpen(false)}
      />

      <AdminNavDropdown
        title="Account"
        icon={User}
        links={adminAccountLinks}
        isExpanded={expandedDropdowns.has('account')}
        onToggle={() => toggleDropdown('account')}
        currentPath={location.pathname}
        onLinkClick={() => setSidebarOpen(false)}
      />

      <AdminNavDropdown
        title="Admin"
        icon={Shield}
        links={adminPanelLinks}
        isExpanded={expandedDropdowns.has('admin')}
        onToggle={() => toggleDropdown('admin')}
        currentPath={location.pathname}
        onLinkClick={() => setSidebarOpen(false)}
      />

      {isSubAdmin && (
        <>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3 px-3 pt-4">Sub-Admin</p>
          <Link
            to="/my-referrals"
            onClick={(e) => { if (isMaintenanceBlocked) { handleMaintenanceClick(e); } else { setSidebarOpen(false); } }}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
              location.pathname === '/my-referrals'
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            )}
          >
            <Link2 className="w-5 h-5" />
            <span className="font-medium">My Referrals</span>
          </Link>
        </>
      )}
    </>
  );

  const renderRegularNavigation = () => (
    <>
      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3 px-3">Main</p>
      {mainNavLinks.map((link) => (
        <div key={link.href}>
          {link.subLinks ? (
            <>
              <button
                onClick={() => setExpandedNav(expandedNav === link.name ? null : link.name)}
                className={cn(
                  "flex items-center justify-between w-full px-3 py-2.5 rounded-lg transition-colors",
                  location.pathname === link.href || location.pathname.startsWith(link.href.replace(/\/$/, ''))
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                )}
              >
                <div className="flex items-center gap-3">
                  <link.icon className="w-5 h-5" />
                  <span className="font-medium">{link.name}</span>
                </div>
                {expandedNav === link.name ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedNav === link.name && (
                <div className="ml-6 mt-1 space-y-1">
                  <Link
                    to={link.href}
                    onClick={() => setSidebarOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm",
                      location.pathname === link.href
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    )}
                  >
                    <link.icon className="w-4 h-4" />
                    <span>All {link.name}</span>
                  </Link>
                  {link.subLinks.map((subLink) => (
                    <Link
                      key={subLink.href}
                      to={subLink.href}
                      onClick={() => setSidebarOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm",
                        location.pathname === subLink.href
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                      )}
                    >
                      <subLink.icon className="w-4 h-4" />
                      <span>{subLink.name}</span>
                    </Link>
                  ))}
                </div>
              )}
            </>
          ) : (
            <Link
              to={link.href}
              onClick={() => setSidebarOpen(false)}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                location.pathname === link.href
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
              )}
            >
              <link.icon className="w-5 h-5" />
              <span className="font-medium">{link.name}</span>
            </Link>
          )}
        </div>
      ))}

      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3 px-3 pt-6">Account</p>
      {accountLinks.map((link) => (
        <Link
          key={link.href}
          to={link.href}
          onClick={() => setSidebarOpen(false)}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            location.pathname === link.href
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
          )}
        >
          <link.icon className="w-5 h-5" />
          <span className="font-medium">{link.name}</span>
        </Link>
      ))}

      {isAmbassador ? (
        <>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3 px-3 pt-6">Ambassador</p>
          <Link
            to="/ambassador"
            onClick={() => setSidebarOpen(false)}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
              location.pathname === '/ambassador'
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            )}
          >
            <Award className="w-5 h-5" />
            <span className="font-medium">Ambassador Hub</span>
          </Link>
        </>
      ) : (
        <Link
          to="/ambassador"
          onClick={() => setSidebarOpen(false)}
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
            location.pathname === '/ambassador'
              ? "bg-primary/10 text-primary"
              : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
          )}
        >
          <Megaphone className="w-5 h-5" />
          <span className="font-medium">Join the Movement</span>
        </Link>
      )}

      {isSubAdmin && (
        <>
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3 px-3 pt-6">Sub-Admin</p>
          <Link
            to="/my-referrals"
            onClick={() => setSidebarOpen(false)}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
              location.pathname === '/my-referrals'
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            )}
          >
            <Link2 className="w-5 h-5" />
            <span className="font-medium">My Referrals</span>
          </Link>
        </>
      )}

      {/* Admin Settings - Only visible to admin users (for non-dropdown view) */}
      {isAdmin && (
        <AdminSettingsPanel 
          currentPath={location.pathname}
          onLinkClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={() => setSidebarOpen(true)}>
            <Menu className="w-6 h-6" />
          </button>
          <Logo size="sm" />
          <div className="flex items-center gap-2">
            <WishlistBadge />
            <ShopifyCartDrawer />
          </div>
        </div>
      </header>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-50 transform transition-transform duration-300 lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="flex flex-col h-full p-6">
          <div className="flex items-center justify-between mb-8">
            <Logo size="sm" />
            <button className="lg:hidden" onClick={() => setSidebarOpen(false)}>
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex-1 space-y-1 overflow-y-auto">
            {!rolesLoaded ? (
              <div className="space-y-2 px-3">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-10 rounded-lg bg-muted/50 animate-pulse" />
                ))}
              </div>
            ) : isAdmin ? renderAdminNavigation() : renderRegularNavigation()}
          </nav>

          <div className="pt-6 border-t border-border">
            <div className="flex items-center gap-3 px-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.email}</p>
                <p className="text-xs text-muted-foreground">
                  {isAdmin ? 'Administrator' : isSubAdmin ? 'Sub-Admin' : 'Member'}
                </p>
              </div>
            </div>
            <Button
              variant="destructive"
              className="w-full justify-start gap-3 shadow-none"
              onClick={handleSignOut}
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        {/* Desktop Header */}
        <header className="hidden lg:flex items-center justify-between px-8 py-4 border-b border-border bg-background/95 backdrop-blur-md sticky top-0 z-40">
          <div>
            <h2 className="text-lg font-medium">
              {isAdmin && location.pathname === '/dashboard' 
                ? 'Admin Control Panel'
                : mainNavLinks.find(l => l.href === location.pathname)?.name || 
                  accountLinks.find(l => l.href === location.pathname)?.name || 
                  (location.pathname.startsWith('/admin') ? 'Admin Control Panel' : 'Dashboard')}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <WishlistBadge />
            <ShopifyCartDrawer />
          </div>
        </header>

        <div className="p-4 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};
